import { AnimatePresence, motion } from 'framer-motion';
import {
    Bell,
    Check,
    CheckCheck,
    Clock,
    File,
    FileText,
    MoreVertical,
    Paperclip,
    Search,
    SendHorizontal,
    Smile,
    Users,
    X,
    Shield
} from 'lucide-react';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { chatAPI } from '../utils/api';
import ConfidentialDocsModal from '../components/chat/ConfidentialDocsModal';

// Helper function to get current user ID
const getCurrentUserId = () => {
  try {
    const token = localStorage.getItem('employer_token');
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.id;
    }
  } catch (error) {
    console.error('Error getting current user ID:', error);
  }
  return null;
};

const Chat = () => {
  const [selectedConversation, setSelectedConversation] = useState(null);

  const selectedConversationRef = useRef(selectedConversation); // FIX: Correctly initialize useRef
  const [employees, setEmployees] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState('');
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
  const [isTaskViewModalOpen, setIsTaskViewModalOpen] = useState(false);
  const [isConfidentialDocsModalOpen, setIsConfidentialDocsModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Computed values
  const filteredEmployees = employees.filter(emp =>
    emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Helper function to get selected employee from conversation
  const getSelectedEmployee = () => {
    if (!selectedConversation) return null;

    const currentUserId = getCurrentUserId();

    // For employer-employee conversations, find the employee participant (not the current user)
    const employeeParticipant = selectedConversation.participants?.find(p =>
      p.userType === 'employee' && p.userId.toString() !== currentUserId
    );

    if (!employeeParticipant) return null;

    return employees.find(emp => emp.id === employeeParticipant.userId.toString()) || null;
  };

  const selectedEmployee = getSelectedEmployee();

  // Initialize chat system
  useEffect(() => {
    initializeChat();
    return () => {
      // Cleanup on unmount - disconnect socket will be handled by the socket listeners cleanup
      console.log('🧹 EMPLOYER: Component unmounting, socket cleanup will be handled by listeners cleanup');
    };
  }, []);

  // Load employees when component mounts
  useEffect(() => {
    loadEmployees();
  }, []);

  // Load messages when conversation changes
  useEffect(() => {
    if (selectedConversation) {
      console.log('🔄 EMPLOYER: selectedConversation changed, loading messages for:', selectedConversation._id);
      
      // FORCE JOIN ROOM FIRST - IMMEDIATE
      console.log('🔗 EMPLOYER: FORCE joining conversation room:', selectedConversation._id);
      if (chatAPI.socket && chatAPI.socket.connected) {
        chatAPI.socket.emit('join-conversation', { conversationId: selectedConversation._id });
        console.log('🔗 EMPLOYER: Join conversation event emitted');
        
        // Test if we can receive the joined-conversation confirmation
        const testJoinedListener = (data) => {
          console.log('🔗 EMPLOYER: TEST - Received joined-conversation confirmation:', data);
        };
        chatAPI.socket.on('joined-conversation', testJoinedListener);
        
        // Test if we can receive message-received events
        const testMessageListener = (data) => {
          console.log('🔗 EMPLOYER: TEST - Received message-received event:', data);
        };
        chatAPI.socket.on('message-received', testMessageListener);
        
      } else {
        console.log('❌ EMPLOYER: Socket not connected!');
      }
      
      loadMessages(selectedConversation._id);
      chatAPI.joinConversation(selectedConversation._id);
      
      // Mark messages as read when conversation is opened
      markMessagesAsRead(selectedConversation._id);
    } else {
      console.log('🔄 EMPLOYER: No selected conversation, clearing messages');
      setChatMessages([]);
    }

    return () => {
      if (selectedConversation) {
        chatAPI.leaveConversation(selectedConversation._id);
      }
    };
  }, [selectedConversation]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    const messageCount = Array.isArray(chatMessages) ? chatMessages.length : 0;
    console.log('📨 EMPLOYER: chatMessages state changed, count:', messageCount, 'Type:', typeof chatMessages, 'Is array:', Array.isArray(chatMessages));
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Set up Socket.IO event listeners
  useEffect(() => {
    if (!chatAPI.socket) {
      console.log('❌ EMPLOYER: No socket available for event listeners');
      return;
    }

    let cleanupFunctions = [];
    let isSetup = false;

    // Set up event listeners only when socket is connected
    const setupListeners = () => {
      if (!chatAPI.socket || !chatAPI.socket.connected || isSetup) {
        console.log('❌ EMPLOYER: Socket not ready or already setup:', {
          socket: !!chatAPI.socket,
          connected: chatAPI.socket?.connected,
          isSetup
        });
        return;
      }

      console.log('✅ EMPLOYER: Setting up socket event listeners');
      console.log('🔌 EMPLOYER: Socket status:', {
        connected: chatAPI.socket?.connected,
        id: chatAPI.socket?.id,
        readyState: chatAPI.socket?.readyState
      });
      isSetup = true;

      // Re-register the primary listener
      const cleanup = chatAPI.onMessageReceived(handleMessageReceived);
      cleanupFunctions.push(cleanup);
      
      console.log('✅ EMPLOYER: Primary "message-received" listener is now active.');

      // The rest of the original listeners
      cleanupFunctions.push(chatAPI.onMessageSent(handleMessageSent));
      cleanupFunctions.push(chatAPI.onMessagesRead(handleMessagesRead));
      cleanupFunctions.push(chatAPI.onReactionAdded(handleReactionAdded));
      cleanupFunctions.push(chatAPI.onReactionRemoved(handleReactionRemoved));
      cleanupFunctions.push(chatAPI.onTypingIndicator(handleTypingIndicator));
      cleanupFunctions.push(chatAPI.onUserStatusChanged(handleUserStatusChanged));
      cleanupFunctions.push(chatAPI.onTaskStatusUpdated(handleTaskStatusUpdated));
      cleanupFunctions.push(chatAPI.onError(handleSocketError));

      // Add raw socket event listeners for debugging
      const debugMessageReceived = (data) => {
        console.log('🔍 EMPLOYER: Raw message-received event received!', {
          messageId: data.message?._id,
          content: data.message?.content,
          senderId: data.message?.senderId,
          senderType: data.message?.senderType,
          conversationId: data.conversationId,
          fullData: data
        });
        
        // FORCE CALL THE ACTUAL HANDLER
        console.log('🚨 EMPLOYER: FORCE CALLING handleMessageReceived from debug listener!');
        handleMessageReceived(data);
      };
      const debugMessageSent = (data) => {
        console.log('🔍 EMPLOYER: Raw message-sent event:', data);
      };
      
      chatAPI.socket.on('message-received', debugMessageReceived);
      chatAPI.socket.on('message-sent', debugMessageSent);
      
      // Add joined-conversation listener
      const debugJoinedConversation = (data) => {
        console.log('🔗 EMPLOYER: Raw joined-conversation event:', data);
      };
      chatAPI.socket.on('joined-conversation', debugJoinedConversation);
      
      // Add catch-all listener to see ANY socket events
      const originalEmit = chatAPI.socket.emit;
      chatAPI.socket.emit = function(event, ...args) {
        console.log('📤 EMPLOYER: Socket emit:', event, args);
        return originalEmit.apply(this, [event, ...args]);
      };
      
      // Test if socket can receive events
      console.log('🧪 EMPLOYER: Testing socket by emitting test event...');
      chatAPI.socket.emit('test-event', { test: 'data' });
      
      // Test if we can receive ANY events
      chatAPI.socket.on('test-response', (data) => {
        console.log('🧪 EMPLOYER: Received test response:', data);
      });
      
      // Test if we can receive the joined-conversation event
      chatAPI.socket.on('joined-conversation', (data) => {
        console.log('🧪 EMPLOYER: Received joined-conversation:', data);
      });
      
      // Test if we can receive ANY events at all
      const originalOn = chatAPI.socket.on;
      chatAPI.socket.on = function(event, callback) {
        console.log('📝 EMPLOYER: Registering listener for event:', event);
        const wrappedCallback = function(...args) {
          console.log('🎯 EMPLOYER: Event received:', event, args);
          if (event === 'message-received') {
            console.log('🚨 EMPLOYER: CRITICAL - message-received event received!');
          }
          return callback.apply(this, args);
        };
        return originalOn.call(this, event, wrappedCallback);
      };
      
      // Test direct message-received listener
      const testMessageReceived = (data) => {
        console.log('🧪 EMPLOYER: TEST message-received listener triggered!', data);
      };
      chatAPI.socket.on('message-received', testMessageReceived);
      console.log('🧪 EMPLOYER: Test message-received listener registered');
      
      // Override the socket's _on method to catch all events (removed duplicate)
      
      cleanupFunctions.push(() => {
        if (chatAPI.socket) {
          chatAPI.socket.off('message-received', debugMessageReceived);
          chatAPI.socket.off('message-sent', debugMessageSent);
          chatAPI.socket.off('joined-conversation', debugJoinedConversation);
        }
      });

      // Add catch-all event listener for debugging
      const catchAllEvents = (eventName, ...args) => {
        if (eventName === 'message-received') {
          console.log('🚨 EMPLOYER: CRITICAL - message-received event caught by catch-all!', args);
        }
        console.log('🔍 EMPLOYER: Socket event received:', eventName, args);
      };
      
      chatAPI.socket.onAny(catchAllEvents);
      cleanupFunctions.push(() => {
        if (chatAPI.socket) {
          chatAPI.socket.offAny(catchAllEvents);
        }
      });
    };

    // If socket is already connected, set up listeners immediately
    if (chatAPI.socket.connected) {
      console.log('✅ EMPLOYER: Socket already connected, setting up listeners');
      setupListeners();
    } else {
      console.log('⏳ EMPLOYER: Waiting for socket connection');
      // Wait for connection
      chatAPI.socket.on('connect', () => {
        console.log('✅ EMPLOYER: Socket connected, setting up listeners');
        setupListeners();
      });
    }

    return () => {
      console.log('🧹 EMPLOYER: Cleaning up socket event listeners');
      // Clean up all event listeners
      cleanupFunctions.forEach(cleanup => {
        if (typeof cleanup === 'function') {
          cleanup();
        }
      });
      cleanupFunctions = [];
      isSetup = false;
      
      // Disconnect socket after cleaning up listeners
      if (chatAPI.socket) {
        console.log('🔌 EMPLOYER: Disconnecting socket after cleanup');
        chatAPI.disconnectSocket();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize Socket.IO connection
  const initializeChat = async () => {
    try {
      const socket = chatAPI.initSocket();
      if (!socket) {
        console.error('Failed to initialize chat socket');
        toast.error('Failed to initialize chat connection');
        return;
      }

      console.log('✅ EMPLOYER: Socket initialized successfully');
      console.log('🔍 EMPLOYER: Socket connection status:', {
        connected: socket.connected,
        id: socket.id,
        transport: socket.io?.engine?.transport?.name || 'unknown'
      });

      // Add connection status logging
      socket.on('connect', () => {
        console.log('✅ EMPLOYER: Socket connected successfully');
        console.log('🔍 EMPLOYER: Socket details:', {
          id: socket.id,
          transport: socket.io?.engine?.transport?.name || 'unknown'
        });
      });

      socket.on('disconnect', (reason) => {
        console.log('❌ EMPLOYER: Socket disconnected:', reason);
        toast.error('Connection lost. Attempting to reconnect...');
      });

      socket.on('connect_error', (error) => {
        console.error('❌ EMPLOYER: Socket connection error:', error);
        toast.error('Failed to connect to chat server');
      });

      // Test socket connection after 2 seconds
      setTimeout(() => {
        console.log('🔍 EMPLOYER: Socket status after 2 seconds:', {
          connected: socket.connected,
          id: socket.id,
          transport: socket.io?.engine?.transport?.name || 'unknown'
        });
        
        // Test if we can emit events
        if (socket.connected) {
          console.log('🧪 EMPLOYER: Testing socket by emitting test event');
          socket.emit('test-event', { message: 'Hello from employer' });
        }
      }, 2000);

    } catch (error) {
      console.error('Failed to initialize chat:', error);
      toast.error('Failed to connect to chat server');
    }
  };



  // Load employees for creating new conversations
  const loadEmployees = async () => {
    try {
      // Import employeeAPI here to avoid circular dependency
      const { employeeAPI } = await import('../utils/api');
      const response = await employeeAPI.getEmployees({ limit: 100 });
      const employeeData = response.data.data.employees || [];
      console.log('🔍 EMPLOYER: Employee data received:', employeeData);
      
      setEmployees(employeeData.map(emp => {
        console.log('🔍 EMPLOYER: Processing employee:', {
          _id: emp._id,
          firstName: emp.firstName,
          lastName: emp.lastName,
          fullName: emp.fullName,
          name: emp.name
        });
        
        return {
          id: emp._id.toString(),
          name: emp.fullName || emp.name || `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || 'Unknown Employee',
          role: emp.position || emp.role || 'Employee',
          department: emp.department || 'General',
          avatar: emp.profilePicture?.url || null,
          email: emp.email,
          status: 'offline', // Default status
          lastMessage: '',
          lastMessageTime: '',
          unreadCount: 0,
          typing: false
        };
      }));
      setLoading(false); // Set loading to false after employees are loaded
    } catch (error) {
      console.error('Error loading employees:', error);
      toast.error('Failed to load employees');
      setLoading(false); // Set loading to false even on error
    }
  };

  // Mark messages as read
  const markMessagesAsRead = async (conversationId) => {
    try {
      console.log('📖 EMPLOYER: Marking messages as read for conversation:', conversationId);
      await chatAPI.markMessagesAsRead(conversationId);
      
      // Note: Conversations list removed, no need to update unread count
      
      console.log('📖 EMPLOYER: Messages marked as read, unread count cleared');
    } catch (error) {
      console.error('❌ EMPLOYER: Error marking messages as read:', error);
    }
  };

  // Load messages for a conversation
  const loadMessages = async (conversationId) => {
    console.log('🔄 EMPLOYER: Loading messages for conversation:', conversationId);
    setMessagesLoading(true);
    try {
      const response = await chatAPI.getMessages(conversationId);
      console.log('📨 EMPLOYER: Messages API response:', response);
      
      // Handle different response formats
      let messages = [];
      if (response && response.data && response.data.data && response.data.data.messages) {
        messages = response.data.data.messages;
      } else if (response && response.data && Array.isArray(response.data)) {
        messages = response.data;
      } else if (response && Array.isArray(response)) {
        messages = response;
      }
      
      console.log('📨 EMPLOYER: Extracted messages:', messages);
      console.log('📨 EMPLOYER: Messages count:', messages.length);
      console.log('📨 EMPLOYER: Messages type:', typeof messages, 'Is array:', Array.isArray(messages));
      
      // Sort messages by creation time to ensure proper order
      const sortedMessages = Array.isArray(messages) ? messages.sort((a, b) => {
        const timeA = new Date(a.createdAt || a.timestamp || 0);
        const timeB = new Date(b.createdAt || b.timestamp || 0);
        return timeA - timeB;
      }) : [];
      
      setChatMessages(sortedMessages);
      console.log('✅ EMPLOYER: Messages loaded and set in state');
    } catch (error) {
      console.error('❌ EMPLOYER: Error loading messages:', error);
      setChatMessages([]);
      toast.error('Failed to load messages');
    } finally {
      setMessagesLoading(false);
    }
  };

  // Keep a ref in sync with the latest selectedConversation state
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  // Handle incoming messages
  const handleMessageReceived = useCallback((data) => {
    const { message, conversationId } = data;
    const currentConversation = selectedConversationRef.current; // Use the ref to get the latest value
    const currentUserId = getCurrentUserId();

    console.log('📬 EMPLOYER: "message-received" event handled.', { 
      msgId: message._id, 
      convoId: conversationId,
      selectedConvoId: currentConversation?._id,
      senderId: message.senderId,
      currentUserId
    });

    // Check if this message is from the current user - if so, skip adding to chat
    // (the user already has it from message-sent event)
    const isFromCurrentUser = message.senderId === currentUserId || 
                             message.senderId?.toString() === currentUserId ||
                             (typeof message.senderId === 'object' && message.senderId?._id?.toString() === currentUserId);

    if (isFromCurrentUser) {
      console.log('ℹ️ EMPLOYER: Message is from current user, skipping add to chat (already handled by message-sent)');
    }

    // Note: Conversations list removed, no need to update conversation list

    // CRITICAL CHECK: Update the main chat window ONLY if the received message belongs to the currently open conversation.
    // AND it's NOT from the current user (to prevent duplicates)
    if (currentConversation && currentConversation._id.toString() === conversationId.toString() && !isFromCurrentUser) {
      console.log('✅ EMPLOYER: Match found! Appending message to main chat window.');
      setChatMessages(prevMessages => {
        // Prevent duplicates
        if (prevMessages.some(m => m._id === message._id)) {
          console.log('⚠️ EMPLOYER: Duplicate message detected. Skipping append.', { msgId: message._id });
          return prevMessages;
        }
        return [...prevMessages, message];
      });
    } else {
      console.log('ℹ️ EMPLOYER: Message is for a different conversation or from current user. Not appending to main chat window.');
    }
  }, []); // No dependencies - this function is stable and uses a ref

  // Handle message sent confirmation
  const handleMessageSent = useCallback((data) => {
    const { message, messageId } = data;
    
    console.log('✅ EMPLOYER: Message sent confirmation:', {
      messageId,
      content: message.content,
      status: message.status
    });

    setChatMessages(prev => {
      // First, try to replace temporary message
      const tempMessageIndex = prev.findIndex(m => m.isTemporary && m.content === message.content);
      
      if (tempMessageIndex !== -1) {
        // Replace temporary message with real message
        console.log('✅ EMPLOYER: Replacing temporary message with real message:', messageId);
        const newMessages = [...prev];
        newMessages[tempMessageIndex] = { ...message, status: 'sent' };
        return newMessages;
      }
      
      // Check if message already exists (fallback)
      const exists = prev.some(m => 
        m._id === messageId || 
        m.id === messageId || 
        m._id === message._id || 
        m.id === message._id
      );
      
      if (exists) {
        // Update existing message status
        console.log('✅ EMPLOYER: Updating existing message status to sent:', messageId);
        return prev.map(m => 
          (m._id === messageId || m.id === messageId || m._id === message._id || m.id === message._id) 
            ? { ...m, status: 'sent' } 
            : m
        );
      } else {
        // Add new message if it doesn't exist
        console.log('✅ EMPLOYER: Adding sent message to chat:', messageId);
        return [...prev, { ...message, status: 'sent' }];
      }
    });
    setSendingMessage(false);
  }, []);

  // Handle messages read
  const handleMessagesRead = (data) => {
    const { messageIds, conversationId } = data;
    
    console.log('📖 EMPLOYER: Messages read event received:', {
      messageIds,
      conversationId,
      currentConversation: selectedConversation?._id
    });

    // Only update if it's for the current conversation
    if (selectedConversation && selectedConversation._id === conversationId) {
      setChatMessages(prev => {
        const updated = prev.map(m => {
          if (messageIds.includes(m._id)) {
            console.log('📖 EMPLOYER: Updating message status to read:', m._id);
            return { ...m, status: 'read' };
          }
          return m;
        });
        return updated;
      });
    }
  };

  // Handle reactions
  const handleReactionAdded = (data) => {
    const { messageId, reaction } = data;
    setChatMessages(prev => prev.map(m =>
      m._id === messageId ? {
        ...m,
        reactions: [...(m.reactions || []), reaction]
      } : m
    ));
  };

  const handleReactionRemoved = (data) => {
    const { messageId, emoji, userId } = data;
    setChatMessages(prev => prev.map(m =>
      m._id === messageId ? {
        ...m,
        reactions: (m.reactions || []).filter(r =>
          !(r.emoji === emoji && r.userId === userId)
        )
      } : m
    ));
  };

  // Handle typing indicators
  const handleTypingIndicator = (data) => {
    const { conversationId, isTyping } = data;
    if (selectedConversation && selectedConversation._id === conversationId) {
      setIsTyping(isTyping);
    }
  };

  // Handle user status changes
  const handleUserStatusChanged = (data) => {
    const { userId, status } = data;
    setEmployees(prev => prev.map(emp =>
      emp.id === userId ? { ...emp, status } : emp
    ));
  };

  // Handle task status updates
  const handleTaskStatusUpdated = useCallback((data) => {
    const { messageId, task } = data;
    
    // Update the task message in chat messages
    setChatMessages(prev => prev.map(msg => 
      msg._id === messageId 
        ? { ...msg, metadata: { ...msg.metadata, task } }
        : msg
    ));
    
    // Show success message
    toast.success('Task status updated!');
  }, []);

  // Handle socket errors
  const handleSocketError = (error) => {
    console.error('Socket error:', error);
    console.error('Socket error details:', {
      message: error.message,
      type: error.type,
      description: error.description,
      context: error.context
    });
    toast.error('Connection error: ' + (error.message || error.type || 'Unknown error'));
  };

  // Send message
  const handleSendMessage = async () => {
    if (!message.trim() || !selectedConversation) return;

    try {
      setSendingMessage(true);
      
      // Create a temporary message for immediate UI feedback
      const tempMessage = {
        _id: `temp-${Date.now()}`,
        content: message,
        senderId: getCurrentUserId(),
        senderType: 'employer',
        messageType: 'text',
        status: 'sending',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        createdAt: new Date(),
        isTemporary: true
      };

      // Add temporary message to UI immediately
      setChatMessages(prev => [...prev, tempMessage]);
      
      // Send message via socket
      chatAPI.sendMessageSocket(
        selectedConversation._id,
        message,
        'text',
        {},
        null
      );
      
      setMessage('');

      // Stop typing indicator
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        chatAPI.stopTyping(selectedConversation._id);
      }

      // Fallback: If no confirmation received within 5 seconds, reload messages
      setTimeout(() => {
        if (sendingMessage) {
          console.log('🔄 EMPLOYER: No message confirmation received, reloading messages...');
          loadMessages(selectedConversation._id);
          setSendingMessage(false);
        }
      }, 5000);

    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
      setSendingMessage(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedConversation) return;

    let loadingToast = null;

    // Validate file type
    const allowedTypes = [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'application/zip',
      'application/x-zip-compressed'
    ];

    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Only images, documents, and archives are allowed.');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('File size too large. Maximum size is 10MB.');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    try {
      setSendingMessage(true);
      loadingToast = toast.loading(`Uploading ${file.name}...`);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('conversationId', selectedConversation._id);
      formData.append('messageType', 'file');

      const response = await chatAPI.uploadFile(formData);
      console.log('File upload response:', response);
      
      // Dismiss loading toast and show success
      toast.dismiss(loadingToast);
      toast.success('File uploaded successfully');

      // Reload messages to show the uploaded file
      if (selectedConversation) {
        console.log('🔄 EMPLOYER: Reloading messages after file upload');
        await loadMessages(selectedConversation._id);
      }

      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      
      // Dismiss loading toast and show error
      toast.dismiss(loadingToast);
      const errorMessage = error.response?.data?.message || 'Failed to upload file';
      toast.error(errorMessage);
    } finally {
      setSendingMessage(false);
    }
  };

  // Handle typing
  const handleTyping = () => {
    if (!selectedConversation || !message.trim()) return;

    chatAPI.startTyping(selectedConversation._id);

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set timeout to stop typing indicator
    typingTimeoutRef.current = setTimeout(() => {
      chatAPI.stopTyping(selectedConversation._id);
    }, 2000);
  };

  // Handle message input change with typing indicator
  const handleMessageChange = (e) => {
    setMessage(e.target.value);
    handleTyping();
  };



  // Force reload messages
  const reloadMessages = async () => {
    if (selectedConversation) {
      console.log('🔄 EMPLOYER: Force reloading messages for conversation:', selectedConversation._id);
      await loadMessages(selectedConversation._id);
      toast.success('Messages refreshed');
    }
  };

  // Handle creating new conversation
  const handleCreateConversation = async (employee) => {
    try {
      console.log('🔄 EMPLOYER: Creating conversation with employee:', {
        employeeId: employee.id,
        employeeName: employee.name,
        participantType: 'employee'
      });

      const response = await chatAPI.createConversation({
        participantId: employee.id,
        participantType: 'employee'
      });

      console.log('✅ EMPLOYER: Conversation created successfully:', response.data);

      if (response.data.data.conversation) {
        setSelectedConversation(response.data.data.conversation);
        toast.success('Conversation started');
      }
    } catch (error) {
      console.error('❌ EMPLOYER: Error creating conversation:', {
        error: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });
      toast.error('Failed to start conversation');
    }
  };

  const addTaskMessage = async (taskData) => {
    if (!selectedConversation) {
      toast.error('No conversation selected');
      return;
    }

    try {
      // Create task message content
      const taskContent = `📋 Task Assigned: ${taskData.taskTitle}`;
      
      // Create task metadata
      const taskMetadata = {
        task: {
          taskTitle: taskData.taskTitle,
          taskDescription: taskData.taskDescription,
          dueDate: new Date(taskData.dueDate),
          priority: taskData.priority,
          status: 'pending',
          assignedTo: {
            userId: selectedEmployee?.id,
            userType: 'employee'
          }
        }
      };

      // Send task message via socket
      chatAPI.sendMessageSocket(
        selectedConversation._id,
        taskContent,
        'task',
        taskMetadata,
        null
      );

      toast.success(`Task assigned to ${selectedEmployee?.name}`);
    } catch (error) {
      console.error('Error sending task message:', error);
      toast.error('Failed to assign task');
    }
  };

  const addReminderMessage = async (reminderData) => {
    if (!selectedConversation) {
      toast.error('No conversation selected');
      return;
    }

    try {
      // Create reminder message content
      const reminderContent = `⏰ Reminder: ${reminderData.reminderTitle}`;
      
      // Create reminder metadata
      const reminderMetadata = {
        reminder: {
          reminderTitle: reminderData.reminderTitle,
          reminderDate: new Date(reminderData.reminderDate),
          reminderTime: reminderData.reminderTime,
          reminderType: reminderData.reminderType,
          isSent: false
        }
      };

      // Send reminder message via socket
      chatAPI.sendMessageSocket(
        selectedConversation._id,
        reminderContent,
        'reminder',
        reminderMetadata,
        null
      );

      toast.success(`Reminder set for ${selectedEmployee?.name}`);
    } catch (error) {
      console.error('Error sending reminder message:', error);
      toast.error('Failed to set reminder');
    }
  };

  // Handle task click to view task details
  const handleTaskClick = (taskMessage) => {
    console.log('🔍 EMPLOYER: Task clicked:', taskMessage);
    
    // Extract task data from message
    const taskData = taskMessage.metadata?.task || {
      taskTitle: taskMessage.taskTitle,
      taskDescription: taskMessage.taskDescription,
      dueDate: taskMessage.dueDate,
      priority: taskMessage.priority,
      status: 'pending'
    };
    
    // Add message ID for status updates
    taskData.messageId = taskMessage._id;
    
    console.log('🔍 EMPLOYER: Extracted task data:', taskData);
    
    setSelectedTask(taskData);
    setIsTaskViewModalOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return 'bg-green-500';
      case 'away': return 'bg-yellow-500';
      case 'offline': return 'bg-gray-400';
      default: return 'bg-gray-400';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'online': return 'Online';
      case 'away': return 'Away';
      case 'offline': return 'Offline';
      default: return 'Offline';
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 }
    }
  };

  const messageVariants = {
    hidden: { opacity: 0, y: 10, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 }
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="h-full flex bg-white rounded-xl shadow-soft overflow-hidden"
      style={{ height: 'calc(100vh - 120px)', maxHeight: 'calc(100vh - 120px)' }}
    >
      {/* Employee List Sidebar */}
      <motion.div
        variants={itemVariants}
        className="w-80 border-r border-gray-100 bg-gray-50 flex flex-col"
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-100 bg-white">
          <h2 className="text-xl font-bold text-gray-900 font-heading">Team Chat</h2>
          <p className="text-sm text-gray-600 mt-1">Communicate with your team</p>
        </div>

        {/* Search */}
        <div className="p-4 bg-white border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search team members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
            />
          </div>
        </div>

                {/* Conversations and Employees List */}
        <div className="flex-1 overflow-y-auto employee-list-scroll">
          {/* Loading Indicator */}
          {loading && (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
              <span className="ml-2 text-gray-600">Loading conversations...</span>
            </div>
          )}
          


          {/* Available Employees */}
          {!loading && (
          <div>
            <h4 className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
              Start New Conversation
            </h4>
            {filteredEmployees.map((employee) => (
              <motion.div
                key={employee.id}
                whileHover={{ backgroundColor: '#f8fafc' }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleCreateConversation(employee)}
                className="p-4 cursor-pointer border-b border-gray-100 transition-all duration-200 hover:bg-gray-50"
              >
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <img
                      src={employee.avatar || 'https://via.placeholder.com/48'}
                      alt={employee.name}
                      className="w-12 h-12 rounded-full object-cover ring-2 ring-white shadow-sm"
                    />
                    <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${getStatusColor(employee.status)} animate-pulse`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-gray-900 truncate">{employee.name}</h3>
                    </div>
                    <p className="text-xs text-gray-600 truncate">{employee.role}</p>
                    <p className="text-xs text-gray-500 truncate mt-1">{employee.department}</p>
                  </div>
                  <div className="text-xs text-primary-600 font-medium">
                    Start Chat
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          )}
        </div>
      </motion.div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col overflow-hidden" style={{ maxHeight: 'calc(100vh - 120px)', margin: 0, padding: 0 }}>
        {selectedEmployee ? (
          <>
            {/* Chat Header */}
            <motion.div
              variants={itemVariants}
              className="p-4 border-b border-gray-100 bg-white flex items-center justify-between shadow-sm"
            >
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <img
                    src={selectedEmployee.avatar}
                    alt={selectedEmployee.name}
                    className="w-12 h-12 rounded-full object-cover ring-2 ring-white shadow-sm"
                  />
                  <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${getStatusColor(selectedEmployee.status)} animate-pulse`} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{selectedEmployee.name}</h3>
                  <p className="text-sm text-gray-600">{selectedEmployee.role} • {getStatusText(selectedEmployee.status)}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={reloadMessages}
                  className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                  title="Refresh Messages"
                >
                  <div className="flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span className="text-sm font-medium">Refresh</span>
                  </div>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsTaskModalOpen(true)}
                  className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                  title="Assign Task"
                >
                  <div className="flex items-center space-x-2">
                    <FileText size={20} />
                    <span className="text-sm font-medium">Assign Task</span>
                  </div>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsReminderModalOpen(true)}
                  className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                  title="Set Reminder"
                >
                  <div className="flex items-center space-x-2">
                    <Bell size={20} />
                    <span className="text-sm font-medium">Set Reminder</span>
                  </div>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsConfidentialDocsModalOpen(true)}
                  className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                  title="Confidential Documents"
                >
                  <div className="flex items-center space-x-2">
                    <Shield size={20} />
                    <span className="text-sm font-medium">Confidential Docs</span>
                  </div>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <MoreVertical size={20} />
                </motion.button>
              </div>
            </motion.div>

            {/* Chat Messages */}
            <motion.div
              variants={itemVariants}
              className="flex-1 overflow-y-auto p-4 space-y-3 bg-gradient-to-br from-gray-50 to-white chat-messages-scroll"
              style={{
                height: 'calc(100vh - 320px)',
                minHeight: '400px'
              }}
            >
              {/* Loading Indicator */}
              {messagesLoading && (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  <span className="ml-2 text-gray-600">Loading messages...</span>
                </div>
              )}
              <AnimatePresence>
                {(Array.isArray(chatMessages) ? chatMessages : []).map((msg) => {
                  const currentUserId = getCurrentUserId();
                  const isCurrentUser = msg.senderId === currentUserId || 
                                       msg.senderId === 'currentUser' ||
                                       msg.senderId?.toString() === currentUserId ||
                                       (msg.senderId && msg.senderId._id && msg.senderId._id.toString() === currentUserId) ||
                                       (typeof msg.senderId === 'object' && msg.senderId._id && msg.senderId._id.toString() === currentUserId);
                  
                  // Debug task messages
                  if (msg.messageType === 'task') {
                    console.log('🔍 EMPLOYER: Task message found:', {
                      messageId: msg._id,
                      messageType: msg.messageType,
                      taskData: msg.metadata?.task,
                      isCurrentUser
                    });
                  }
                  
                  // Debug logging
                  console.log('🔍 EMPLOYER: Message alignment check:', {
                    messageId: msg._id || msg.id,
                    msgSenderId: msg.senderId,
                    msgSenderIdType: typeof msg.senderId,
                    currentUserId,
                    currentUserIdType: typeof currentUserId,
                    isCurrentUser,
                    senderType: msg.senderType
                  });
                  
                  return (
                    <motion.div
                      key={msg._id || msg.id}
                      variants={messageVariants}
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                      className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                    >
                    <motion.div
                       whileHover={{ scale: 1.02 }}
                       className={`max-w-xs lg:max-w-md px-4 py-2.5 rounded-2xl shadow-sm ${
                         isCurrentUser
                           ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white'
                           : 'bg-white text-gray-900 border border-gray-100'
                       }`}
                     >
                       {msg.messageType === 'file' ? (
                         <div className="flex flex-col space-y-2">
                           {console.log('📁 EMPLOYER: File message metadata:', {
                             messageType: msg.messageType,
                             fileMetadata: msg.metadata?.file,
                             fileType: msg.metadata?.file?.fileType,
                             fileName: msg.metadata?.file?.fileName
                           })}
                           <div className="flex items-center space-x-3">
                             {msg.metadata?.file?.fileType?.startsWith('image/') ? (
                               <img 
                                 src={`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/chat/serve/${msg.metadata.file.publicId}`}
                                 alt={msg.metadata.file.fileName}
                                 className="w-12 h-12 object-cover rounded-lg border"
                                 onError={(e) => {
                                   e.target.style.display = 'none';
                                   e.target.nextSibling.style.display = 'flex';
                                 }}
                               />
                             ) : (
                               <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                                 <File size={20} className="text-gray-600" />
                               </div>
                             )}
                             <div className="flex-1 min-w-0">
                               <p className="text-sm font-medium truncate">
                                 {msg.metadata?.file?.fileName || 'Unknown file'}
                               </p>
                               <p className="text-xs text-gray-500">
                                 {(msg.metadata?.file?.fileSize / 1024 / 1024).toFixed(2)} MB
                               </p>
                             </div>
                           </div>
                           <div className="flex space-x-2">
                             <button
                               onClick={() => {
                                 console.log('📥 EMPLOYER: Downloading file:', {
                                   publicId: msg.metadata?.file?.publicId,
                                   fileName: msg.metadata?.file?.fileName
                                 });
                                 
                                 // Create download with authentication
                                 const token = localStorage.getItem('employer_token');
                                 const downloadUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/chat/serve/${msg.metadata?.file?.publicId}`;
                                 
                                 console.log('🔐 EMPLOYER: Auth debug:', {
                                   token: token ? `${token.substring(0, 20)}...` : 'NO TOKEN',
                                   tokenLength: token ? token.length : 0,
                                   downloadUrl: downloadUrl,
                                   headers: {
                                     'Authorization': `Bearer ${token}`,
                                     'Content-Type': 'application/json'
                                   }
                                 });
                                 
                                 fetch(downloadUrl, {
                                   method: 'GET',
                                   headers: {
                                     'Authorization': `Bearer ${token}`,
                                     'Content-Type': 'application/json'
                                   }
                                 })
                                 .then(response => {
                                   if (!response.ok) {
                                     throw new Error('Download failed');
                                   }
                                   return response.blob();
                                 })
                                 .then(blob => {
                                   const url = window.URL.createObjectURL(blob);
                                   const a = document.createElement('a');
                                   a.href = url;
                                   a.download = msg.metadata?.file?.fileName || 'download';
                                   document.body.appendChild(a);
                                   a.click();
                                   window.URL.revokeObjectURL(url);
                                   document.body.removeChild(a);
                                 })
                                 .catch(error => {
                                   console.error('❌ EMPLOYER: Download error:', error);
                                   toast.error('Failed to download file');
                                 });
                               }}
                               className="px-3 py-1 bg-blue-100 text-blue-700 text-xs rounded-full hover:bg-blue-200 transition-colors cursor-pointer"
                             >
                               📥 Download
                             </button>
                             {msg.metadata?.file?.fileType?.startsWith('image/') && (
                               <button
                                 onClick={() => {
                                   console.log('🖼️ EMPLOYER: Opening image:', {
                                     fileType: msg.metadata?.file?.fileType,
                                     publicId: msg.metadata?.file?.publicId
                                   });
                                   
                                   // Create authenticated image view
                                   const token = localStorage.getItem('employer_token');
                                   const imageUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/chat/serve/${msg.metadata.file.publicId}`;
                                   
                                   fetch(imageUrl, {
                                     method: 'GET',
                                     headers: {
                                       'Authorization': `Bearer ${token}`,
                                       'Content-Type': 'application/json'
                                     }
                                   })
                                   .then(response => {
                                     if (!response.ok) {
                                       throw new Error('Failed to load image');
                                     }
                                     return response.blob();
                                   })
                                   .then(blob => {
                                     const url = window.URL.createObjectURL(blob);
                                     window.open(url, '_blank');
                                     // Clean up the URL after a delay
                                     setTimeout(() => window.URL.revokeObjectURL(url), 1000);
                                   })
                                   .catch(error => {
                                     console.error('❌ EMPLOYER: Image view error:', error);
                                     toast.error('Failed to view image');
                                   });
                                 }}
                                 className="px-3 py-1 bg-green-100 text-green-700 text-xs rounded-full hover:bg-green-200 transition-colors cursor-pointer"
                               >
                                 👁️ View
                               </button>
                             )}
                           </div>
                         </div>
                       ) : msg.messageType === 'task' ? (
                         <div className="flex flex-col">
                           <div className="flex items-center justify-between mb-2">
                             <div className="flex items-center space-x-2">
                               <FileText size={16} className={isCurrentUser ? 'text-red-100' : 'text-red-600'} />
                               <span className={`text-sm font-semibold ${isCurrentUser ? 'text-red-100' : 'text-red-600'}`}>
                                 📋 {msg.metadata?.task ? 'Task' : 'Task Request'}
                               </span>
                             </div>
                             {/* Status Indicator */}
                             <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
                               (msg.metadata?.task?.status || 'pending') === 'completed' ? 'bg-green-100 text-green-800' :
                               (msg.metadata?.task?.status || 'pending') === 'in-progress' ? 'bg-blue-100 text-blue-800' :
                               (msg.metadata?.task?.status || 'pending') === 'cancelled' ? 'bg-red-100 text-red-800' :
                               'bg-yellow-100 text-yellow-800'
                             }`}>
                               <span>
                                 {(msg.metadata?.task?.status || 'pending') === 'completed' ? '✅' :
                                  (msg.metadata?.task?.status || 'pending') === 'in-progress' ? '🔄' :
                                  (msg.metadata?.task?.status || 'pending') === 'cancelled' ? '❌' :
                                  '⏳'}
                               </span>
                               <span>
                                 {(msg.metadata?.task?.status || 'pending').charAt(0).toUpperCase() + (msg.metadata?.task?.status || 'pending').slice(1)}
                               </span>
                             </div>
                           </div>
                           <div 
                             className={`p-3 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-md ${
                               isCurrentUser 
                                 ? 'bg-red-500/20 border-red-300/30 hover:bg-red-500/30' 
                                 : 'bg-red-50 border-red-200 hover:bg-red-100'
                             }`}
                             onClick={() => {
                               handleTaskClick(msg);
                             }}
                             title="Click to view task details"
                           >
                             <h4 className={`font-semibold text-sm mb-1 ${isCurrentUser ? 'text-white' : 'text-red-900'}`}>
                               {msg.metadata?.task?.taskTitle || msg.taskTitle}
                             </h4>
                             <p className={`text-xs mb-2 ${isCurrentUser ? 'text-red-100' : 'text-red-700'}`}>
                               {msg.metadata?.task?.taskDescription || msg.taskDescription}
                             </p>
                             <div className="flex items-center justify-between text-xs">
                               <span className={`${isCurrentUser ? 'text-red-200' : 'text-red-600'}`}>
                                 📅 Due: {new Date(msg.metadata?.task?.dueDate || msg.dueDate).toLocaleDateString()}
                               </span>
                               <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                 (msg.metadata?.task?.priority || msg.priority) === 'high' ? 'bg-red-200 text-red-800' :
                                 (msg.metadata?.task?.priority || msg.priority) === 'medium' ? 'bg-orange-200 text-orange-800' :
                                 'bg-green-200 text-green-800'
                               }`}>
                                 {(msg.metadata?.task?.priority || msg.priority || 'medium').charAt(0).toUpperCase() + (msg.metadata?.task?.priority || msg.priority || 'medium').slice(1)}
                               </span>
                             </div>
                             <div className="mt-2 text-xs text-red-600 font-medium">
                               👆 Click to view full details
                             </div>
                           </div>
                         </div>
                       ) : msg.messageType === 'reminder' ? (
                         <div className="flex flex-col">
                           <div className="flex items-center space-x-2 mb-2">
                             <Bell size={16} className={isCurrentUser ? 'text-yellow-100' : 'text-yellow-600'} />
                             <span className={`text-sm font-semibold ${isCurrentUser ? 'text-yellow-100' : 'text-yellow-600'}`}>
                               ⏰ Reminder
                             </span>
                           </div>
                           <div className={`p-3 rounded-lg border ${isCurrentUser ? 'bg-yellow-500/20 border-yellow-300/30' : 'bg-yellow-50 border-yellow-200'}`}>
                             <h4 className={`font-semibold text-sm mb-1 ${isCurrentUser ? 'text-white' : 'text-yellow-900'}`}>
                               {msg.metadata?.reminder?.reminderTitle || msg.reminderTitle}
                             </h4>
                             <div className="flex items-center justify-between text-xs mb-2">
                               <span className={`${isCurrentUser ? 'text-yellow-200' : 'text-yellow-700'}`}>
                                 📅 {new Date(msg.metadata?.reminder?.reminderDate || msg.reminderDate).toLocaleDateString()}
                               </span>
                               <span className={`${isCurrentUser ? 'text-yellow-200' : 'text-yellow-700'}`}>
                                 🕐 {msg.metadata?.reminder?.reminderTime || msg.reminderTime}
                               </span>
                             </div>
                             <div className={`text-xs px-2 py-1 rounded-full inline-block ${
                               (msg.metadata?.reminder?.reminderType || msg.reminderType) === 'both' ? 'bg-yellow-200 text-yellow-800' :
                               (msg.metadata?.reminder?.reminderType || msg.reminderType) === 'email' ? 'bg-orange-200 text-orange-800' :
                               'bg-gray-200 text-gray-800'
                             }`}>
                               {(msg.metadata?.reminder?.reminderType || msg.reminderType || 'notification').charAt(0).toUpperCase() + (msg.metadata?.reminder?.reminderType || msg.reminderType || 'notification').slice(1)}
                             </div>
                           </div>
                         </div>
                       ) : (
                         <p className="text-sm">{msg.content || msg.text}</p>
                       )}
                      <div className={`flex items-center justify-between mt-2 text-xs ${
                        isCurrentUser ? 'text-primary-100' : 'text-gray-500'
                      }`}>
                        <span>{msg.timestamp || msg.formattedTimestamp || new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        {isCurrentUser && (
                          <div className="flex items-center space-x-1">
                            {(!msg.status || msg.status === 'sending') && <Clock size={12} />}
                            {msg.status === 'sent' && <Check size={12} />}
                            {msg.status === 'read' && <CheckCheck size={12} />}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  </motion.div>
                  );
                })}
              </AnimatePresence>

              {/* Typing Indicator */}
              {isTyping && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-start"
                >
                  <div className="bg-white border border-gray-100 rounded-2xl px-4 py-2.5 shadow-sm">
                    <div className="flex items-center space-x-1">
                      <div className="flex space-x-1">
                        <motion.div
                          animate={{ y: [0, -5, 0] }}
                          transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
                          className="w-2 h-2 bg-gray-400 rounded-full"
                        />
                        <motion.div
                          animate={{ y: [0, -5, 0] }}
                          transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
                          className="w-2 h-2 bg-gray-400 rounded-full"
                        />
                        <motion.div
                          animate={{ y: [0, -5, 0] }}
                          transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
                          className="w-2 h-2 bg-gray-400 rounded-full"
                        />
                      </div>
                      <span className="text-xs text-gray-500 ml-2">typing...</span>
                    </div>
                  </div>
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </motion.div>

            {/* Message Input */}
            <motion.div
              variants={itemVariants}
              className="p-4 border-t border-gray-100 bg-white shadow-lg"
              style={{ margin: 0, paddingBottom: '16px' }}
            >
              <div className="flex items-center space-x-3">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => fileInputRef.current?.click()}
                  className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                  title="Attach file"
                >
                  <Paperclip size={20} />
                </motion.button>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileUpload}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.webp,.zip"
                />
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                  title="Add emoji"
                >
                  <Smile size={20} />
                </motion.button>
                <div className="flex-1 relative">
                  <textarea
                    value={message}
                    onChange={handleMessageChange}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your message..."
                    rows="1"
                    className="w-full px-3 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                  />
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleSendMessage}
                  disabled={!message.trim()}
                  className="p-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg hover:from-primary-700 hover:to-primary-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                >
                  <SendHorizontal size={18} />
                </motion.button>
              </div>
            </motion.div>
          </>
        ) : (
          /* Empty State */
          <motion.div
            variants={itemVariants}
            className="flex-1 flex items-center justify-center bg-gradient-to-br from-gray-50 to-white"
          >
            <div className="text-center">
              <motion.div
                animate={{
                  scale: [1, 1.1, 1],
                  rotate: [0, 5, -5, 0]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="w-24 h-24 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"
              >
                <Users size={40} className="text-primary-600" />
              </motion.div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Select a team member</h3>
              <p className="text-gray-600 max-w-md">Choose someone from your team to start chatting, assign tasks, or set reminders</p>
            </div>
          </motion.div>
        )}
      </div>

      {/* Task Assignment Modal */}
      <AnimatePresence>
        {isTaskModalOpen && (
          <TaskModal
            employee={selectedEmployee}
            onClose={() => setIsTaskModalOpen(false)}
            onTaskCreated={addTaskMessage}
          />
        )}
      </AnimatePresence>

      {/* Reminder Modal */}
      <AnimatePresence>
        {isReminderModalOpen && (
          <ReminderModal
            employee={selectedEmployee}
            onClose={() => setIsReminderModalOpen(false)}
            onReminderCreated={addReminderMessage}
          />
        )}
      </AnimatePresence>

      {/* Task View Modal */}
      <AnimatePresence>
        {isTaskViewModalOpen && (
          <TaskViewModal
            task={selectedTask}
            onClose={() => setIsTaskViewModalOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Confidential Documents Modal */}
      <ConfidentialDocsModal
        isOpen={isConfidentialDocsModalOpen}
        onClose={() => setIsConfidentialDocsModalOpen(false)}
        conversationId={selectedConversation?._id}
        selectedEmployee={(() => {
          if (!selectedConversation?.participants) return null;
          const employeeParticipant = selectedConversation.participants.find(p => p.userType === 'employee');
          if (!employeeParticipant) return null;
          
          // Find the employee in the employees array
          const employee = employees.find(emp => emp.id === employeeParticipant.userId.toString());
          if (!employee) return null;
          
          return {
            _id: employee.id,
            firstName: employee.firstName || 'Employee',
            lastName: employee.lastName || ''
          };
        })()}
        onDocumentUploaded={() => {
          // Optionally refresh messages or show notification
          console.log('Document uploaded successfully');
        }}
      />
    </motion.div>
  );
};

// Task Assignment Modal Component
const TaskModal = ({ employee, onClose, onTaskCreated }) => {
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState('medium');

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle task assignment logic here
    console.log('Task assigned:', { taskTitle, taskDescription, dueDate, priority, employee: employee?.name });

    // Add task message to chat
    const taskData = {
      taskTitle,
      taskDescription,
      dueDate,
      priority
    };

    onTaskCreated(taskData);
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        className="bg-white rounded-xl shadow-elegant p-6 w-full max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Assign Task to {employee?.name}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="form-label">Task Title</label>
            <input
              type="text"
              value={taskTitle}
              onChange={(e) => setTaskTitle(e.target.value)}
              className="input-field"
              placeholder="Enter task title"
              required
            />
          </div>

          <div>
            <label className="form-label">Description</label>
            <textarea
              value={taskDescription}
              onChange={(e) => setTaskDescription(e.target.value)}
              className="input-field resize-none"
              rows="3"
              placeholder="Enter task description"
              required
            />
          </div>

          <div>
            <label className="form-label">Due Date</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="input-field"
              required
            />
          </div>

          <div>
            <label className="form-label">Priority</label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="select-field"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary flex-1"
            >
              Assign Task
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Reminder Modal Component
const ReminderModal = ({ employee, onClose, onReminderCreated }) => {
  const [reminderTitle, setReminderTitle] = useState('');
  const [reminderDate, setReminderDate] = useState('');
  const [reminderTime, setReminderTime] = useState('');
  const [reminderType, setReminderType] = useState('notification');

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle reminder setting logic here
    console.log('Reminder set:', { reminderTitle, reminderDate, reminderTime, reminderType, employee: employee?.name });

    // Add reminder message to chat
    const reminderData = {
      reminderTitle,
      reminderDate,
      reminderTime,
      reminderType
    };

    onReminderCreated(reminderData);
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        className="bg-white rounded-xl shadow-elegant p-6 w-full max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Set Reminder for {employee?.name}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="form-label">Reminder Title</label>
            <input
              type="text"
              value={reminderTitle}
              onChange={(e) => setReminderTitle(e.target.value)}
              className="input-field"
              placeholder="Enter reminder title"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">Date</label>
              <input
                type="date"
                value={reminderDate}
                onChange={(e) => setReminderDate(e.target.value)}
                className="input-field"
                required
              />
            </div>
            <div>
              <label className="form-label">Time</label>
              <input
                type="time"
                value={reminderTime}
                onChange={(e) => setReminderTime(e.target.value)}
                className="input-field"
                required
              />
            </div>
          </div>

          <div>
            <label className="form-label">Reminder Type</label>
            <select
              value={reminderType}
              onChange={(e) => setReminderType(e.target.value)}
              className="select-field"
            >
              <option value="notification">Notification</option>
              <option value="email">Email</option>
              <option value="both">Both</option>
            </select>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary flex-1"
            >
              Set Reminder
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Task View Modal Component (for viewing received tasks)
const TaskViewModal = ({ task, onClose }) => {
  const [selectedStatus, setSelectedStatus] = useState(task?.status || 'pending');
  const [isUpdating, setIsUpdating] = useState(false);

  if (!task) return null;

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'in-progress': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const handleStatusUpdate = async () => {
    if (!task.messageId || selectedStatus === task.status) return;

    setIsUpdating(true);
    try {
      await chatAPI.updateTaskStatus(task.messageId, selectedStatus);
      toast.success('Task status updated successfully!');
      
      // Update the task status locally
      task.status = selectedStatus;
      
      // Close modal after successful update
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error('Failed to update task status');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Task Details</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          <div className="space-y-4">
            {/* Task Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Task Title</label>
              <div className="p-3 bg-gray-50 rounded-lg border">
                <span className="text-gray-900 font-medium">{task.taskTitle}</span>
              </div>
            </div>

            {/* Task Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <div className="p-3 bg-gray-50 rounded-lg border">
                <span className="text-gray-700">{task.taskDescription}</span>
              </div>
            </div>

            {/* Due Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Due Date</label>
              <div className="p-3 bg-gray-50 rounded-lg border">
                <span className="text-gray-700">
                  {new Date(task.dueDate).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
              <div className="p-3 bg-gray-50 rounded-lg border">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getPriorityColor(task.priority)}`}>
                  {task.priority?.charAt(0).toUpperCase() + task.priority?.slice(1) || 'Medium'}
                </span>
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <div className="p-3 bg-gray-50 rounded-lg border">
                <div className="flex items-center justify-between">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(task.status)}`}>
                    {task.status?.charAt(0).toUpperCase() + task.status?.slice(1) || 'Pending'}
                  </span>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="ml-3 px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="pending">Pending</option>
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Assigned By */}
            {task.assignedBy && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Assigned By</label>
                <div className="p-3 bg-gray-50 rounded-lg border">
                  <span className="text-gray-700">{task.assignedBy}</span>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary flex-1"
                disabled={isUpdating}
              >
                Close
              </button>
              <button
                type="button"
                className="btn-primary flex-1"
                onClick={handleStatusUpdate}
                disabled={isUpdating || selectedStatus === task.status}
              >
                {isUpdating ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Chat;
